import { z } from 'zod';

/**
 * SQL injection prevention regex
 * Blocks common SQL injection patterns like:
 * - SQL keywords (SELECT, INSERT, UPDATE, DELETE, DROP, etc.)
 * - SQL comments (-- or /*)
 * - SQL operators (=, <, >, etc. when combined with quotes)
 */
const sqlInjectionPattern = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|CREATE|EXEC|UNION|FROM|WHERE|ORDER BY|GROUP BY)\b|--|\/\*|\*\/|';|";|'=|"=|'<|">|'\+|"\+)/i;

/**
 * Helper function to prevent SQL injection
 */
const preventSqlInjection = (val: string) => !sqlInjectionPattern.test(val);

/**
 * Contact form validation schema
 * Defines validation rules for contact form fields
 */
export const contactInquirySchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[A-Za-z\s\-']+$/, 'Name must contain only letters, spaces, hyphens, and apostrophes'),
  
  email: z.string()
    .email('Please enter a valid email address')
    .max(100, 'Email must be less than 100 characters'),
  
  phone: z.string()
    .regex(/^[0-9]{10}$/, 'Please enter a valid 10-digit phone number'),
  
  industry: z.string()
    .min(1, 'Please select your industry')
    .refine(preventSqlInjection, {
      message: 'Industry contains potentially unsafe characters or patterns'
    }),
  
  state: z.string()
    .min(1, 'Please select your state')
    .refine(preventSqlInjection, {
      message: 'State contains potentially unsafe characters or patterns'
    }),
  
  product: z.string()
    .min(1, 'Please select a product')
    .refine(preventSqlInjection, {
      message: 'Product contains potentially unsafe characters or patterns'
    }),
  
  message: z.string()
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message must be less than 1000 characters')
    .refine(preventSqlInjection, {
      message: 'Message contains potentially unsafe characters or patterns'
    }),
});

/**
 * Export types derived from schema
 */
export type ContactInquiryInput = z.infer<typeof contactInquirySchema>;

/**
 * Contact inquiry response schema
 * Includes server-generated fields
 */
export const contactInquiryResponseSchema = contactInquirySchema.extend({
  id: z.number(),
  created_at: z.string(),
  status: z.enum(['new', 'contacted', 'closed']),
  notes: z.string()
    .optional()
    .refine(val => val === undefined || val === null || preventSqlInjection(val), {
      message: 'Notes contains potentially unsafe characters or patterns'
    }),
});

export type ContactInquiryResponse = z.infer<typeof contactInquiryResponseSchema>; 